// Starting points for a proposal: a package, a retainer, or nothing.
//
// The old builder offered "The ICON Build" and "Brand Foundation" as starting
// templates; they went with ICON on 2026-09-16 and nothing replaced them, so
// every proposal began as seven empty sections. These are the same idea on the
// price list: a preset names services and nothing else. Prices, deliverables
// and schedules are read from the list when the preset is picked, so a preset
// can never quote a stale number, and the combined credit, payment schedule and
// warnings come out of the same arithmetic as adding the services one by one.

import { emptySection, addAtPath, startingSections, withTotals, investmentTotal } from './documentSections'
import { lineFromService } from './priceList'
import { impliedProgram } from './leadPipeline'

export const PRESETS = [
  {
    id: 'brand-foundation',
    group: 'package',
    name: 'Brand Foundation',
    tagline: 'Positioning, the visual identity and the voice: a brand that can be used.',
    services: ['positioning-messaging', 'visual-identity-system', 'brand-voice-guidelines'],
  },
  {
    id: 'complete-brand',
    group: 'package',
    name: 'Complete Brand',
    tagline: 'Brand Foundation, plus the portfolio structure and the templates that roll it out.',
    services: ['positioning-messaging', 'brand-architecture', 'visual-identity-system', 'brand-voice-guidelines', 'templates-rollout'],
  },
  {
    id: 'full-build',
    group: 'package',
    name: 'Full Build',
    tagline: 'The brand and the website and CRM that put it to work. What the ICON Build was.',
    services: ['positioning-messaging', 'visual-identity-system', 'brand-voice-guidelines', 'website-design', 'crm-marketing-automation'],
  },
  {
    id: 'website',
    group: 'package',
    name: 'Website',
    tagline: 'A new site on positioning the client already has.',
    services: ['website-design'],
  },
  {
    id: 'website-growth',
    group: 'package',
    name: 'Website & Growth',
    tagline: 'The site, and the CRM and campaigns that turn visitors into pipeline.',
    services: ['positioning-messaging', 'website-design', 'crm-marketing-automation', 'funnels-campaigns'],
  },
  {
    id: 'positioning-sprint',
    group: 'package',
    name: 'Positioning Sprint',
    tagline: 'Who they serve and why they win, on its own.',
    services: ['positioning-messaging'],
  },
  {
    id: 'fractional-cmo',
    group: 'retainer',
    name: 'Fractional CMO',
    tagline: 'Ongoing marketing leadership, monthly.',
    services: ['fractional-cmo'],
  },
  {
    id: 'brand-care',
    group: 'retainer',
    name: 'Brand Care',
    tagline: 'Keeping the brand system current after launch, monthly.',
    services: ['brand-system-maintenance'],
  },
]

// The clauses every proposal carries, in the order a client reads them. Their
// wording is the price list's own, so changing a policy there changes what new
// proposals say.
const TERMS_FROM_POLICIES = ['payment-terms', 'scope-protection', 'contractor-costs', 'rush-work']

/** A proposal nobody has started: no priced lines, no scope, no opening. */
export function isFreshProposal(doc) {
  if (!doc || doc.kind !== 'proposal') return false
  const sections = doc.sections || []
  const has = (type, test) => sections.some(s => s.type === type && test(s.data || {}))
  return !has('investment', d => (d.rows || []).length > 0)
    && !has('scope', d => (d.items || []).length > 0)
    && !has('overview', d => String(d.body || '').trim().length > 0)
}

/**
 * One service onto the document: its price into Investment, its deliverables
 * into Scope, adding either section if it is missing. The studio's price
 * picker and the presets both go through this, so a preset is exactly the
 * services added by hand.
 */
export function withService(doc, service, { workPackages = [], version = null } = {}) {
  const line = lineFromService(
    { ...service, work_packages: workPackages.filter(w => w.service_id === service.id) },
    { version },
  )
  let next = { ...doc, sections: [...(doc.sections || [])] }
  for (const type of ['scope', 'investment']) {
    if (!next.sections.some(x => x.type === type)) next.sections.push(emptySection(type))
  }
  const inv = next.sections.findIndex(x => x.type === 'investment')
  const scp = next.sections.findIndex(x => x.type === 'scope')
  next = addAtPath(next, `sections.${inv}.data.rows`, line)
  next = addAtPath(next, `sections.${scp}.data.items`, {
    title: line.label,
    description: service.summary || '',
    deliverables: line.work_packages,
    schedule: line.schedule,
    amount: line.amount,
    kind: line.kind,
    service_id: line.service_id,
    program_id: line.program_id,
  })
  return { doc: next, line }
}

const fillSection = (doc, type, fill) => ({
  ...doc,
  sections: (doc.sections || []).map(s => (s.type === type ? { ...s, data: fill(s.data || {}) } : s)),
})

/**
 * A preset onto a fresh proposal.
 *
 * Services the list no longer carries are skipped and reported rather than
 * failing the whole preset. The opening and the terms are filled only where
 * they are still empty: a preset starts a document, it never overwrites one.
 */
export function applyPreset(doc, preset, catalog = {}) {
  const services = catalog.services || []
  const policies = catalog.policies || []
  const programs = catalog.programs || []
  let next = doc
  const added = []
  const missing = []

  for (const id of preset.services) {
    const service = services.find(s => s.id === id)
    if (!service) { missing.push(id); continue }
    const r = withService(next, service, { workPackages: catalog.workPackages || [], version: catalog.version ?? null })
    next = r.doc
    added.push(r.line)
  }

  // The opening: what the package is, then what each program in it is for.
  const programIds = [...new Set(added.map(l => l.program_id).filter(Boolean))]
  const programLines = programIds
    .map(id => programs.find(p => p.id === id))
    .filter(Boolean)
    .map(p => `${p.name}: ${p.summary}`)
  next = fillSection(next, 'overview', d => (String(d.body || '').trim()
    ? d
    : { ...d, body: [preset.tagline, ...programLines].join('\n\n') }))

  const clauses = TERMS_FROM_POLICIES
    .map(id => policies.find(p => p.id === id))
    .filter(p => p?.body)
    .map(p => ({ title: p.name || p.id, body: p.body }))
  if (clauses.length) {
    next = fillSection(next, 'terms', d => ((d.items || []).length ? d : { ...d, items: clauses }))
  }

  // A proposal still called "New Proposal" takes the package's name.
  const untitled = !doc.title || /^new proposal$/i.test(doc.title.trim()) || /^proposal\s+—/i.test(doc.title.trim())
  if (untitled) next = { ...next, title: doc.recipient ? `${preset.name} — ${doc.recipient}` : preset.name }

  return { doc: next, added, missing }
}

/** What a preset comes to before credits, from the list, for the picker. */
export function presetStartingPrice(preset, services = []) {
  let once = 0
  let monthly = 0
  for (const id of preset.services) {
    const s = services.find(x => x.id === id)
    if (!s) continue
    if (s.price_type === 'recurring') monthly += Number(s.price_amount) || 0
    else once += Number(s.price_amount) || 0
  }
  return { once, monthly }
}

/** The names of a preset's services as the list spells them, for its card. */
export const presetServiceNames = (preset, services = []) =>
  preset.services.map(id => services.find(s => s.id === id)?.service).filter(Boolean)

/**
 * The live price list, read the way the studio reads it.
 *
 * The new-proposal dialog and the studio both price from this, so a package
 * picked in the dialog and the same services added by hand in the studio
 * land at the same numbers. Never throws: a failed read is an empty list,
 * and the callers offer "blank" instead of a package.
 */
export async function loadCatalog(client) {
  const empty = { programs: [], services: [], workPackages: [], policies: [], version: null, ...EMPTY_EXTRAS }
  if (!client?.from) return empty
  let base
  try {
    const [p, sv, w, pol, v] = await Promise.all([
      client.from('catalog_programs').select('*').order('sort_order'),
      client.from('catalog_services').select('*').neq('offering', 'archived'),
      client.from('catalog_work_packages').select('*').order('sort_order'),
      client.from('catalog_policies').select('*').order('sort_order'),
      client.from('price_list_versions').select('version').order('version', { ascending: false }).limit(1),
    ])
    const rows = (r) => (Array.isArray(r?.data) ? r.data : [])
    base = {
      programs: rows(p),
      services: rows(sv),
      workPackages: rows(w),
      policies: rows(pol),
      version: rows(v)[0]?.version ?? null,
    }
  } catch {
    return empty
  }
  // What a new proposal's words are written from (lib/proposalGenerate). Read
  // apart from the list, so a missing table here never costs the prices.
  return { ...base, ...(await loadGenerationExtras(client)) }
}

const EMPTY_EXTRAS = { contractors: [], caseStudies: [], testimonials: [], settings: {} }

const parseJsonList = (v) => {
  if (Array.isArray(v)) return v
  try { const x = JSON.parse(v || '[]'); return Array.isArray(x) ? x : [] } catch { return [] }
}

/**
 * Contractors, case studies, testimonials and the owner, for the generator.
 *
 * Case studies come from two places the studio already writes: library pages
 * of kind case_study (proposal_pages) and the bid content's case studies
 * (app_settings.bid_case_studies). Testimonials are bid_testimonials. Each
 * read fails on its own to an empty list.
 */
export async function loadGenerationExtras(client) {
  if (!client?.from) return { ...EMPTY_EXTRAS }
  const read = async (q) => { try { const r = await q(); return r?.error ? [] : (Array.isArray(r?.data) ? r.data : []) } catch { return [] } }
  const [contractors, pages, settings] = await Promise.all([
    read(() => client.from('contractors').select('id, name, specialty, status')),
    read(() => client.from('proposal_pages').select('*').eq('kind', 'case_study')),
    read(() => client.from('app_settings').select('key, value').in('key', ['owner_name', 'owner_email', 'bid_about', 'bid_case_studies', 'bid_testimonials'])),
  ])
  const by = Object.fromEntries(settings.map(r => [r.key, r.value]))
  const caseStudies = [
    ...pages.filter(p => p && p.is_active !== false && String(p.title || '').trim())
      .map(p => ({ id: p.id, title: p.title, body: p.body || '', service_id: p.service_id || null, client: '', category: '', results: [], source: 'library' })),
    ...parseJsonList(by.bid_case_studies).filter(c => c && String(c.title || '').trim())
      .map((c, i) => ({ id: `bid-case-${i}`, title: c.title, body: c.description || '', client: c.client || '', category: c.category || '', results: Array.isArray(c.results) ? c.results.filter(Boolean) : [], service_id: null, source: 'bids' })),
  ]
  return {
    contractors,
    caseStudies,
    testimonials: parseJsonList(by.bid_testimonials).filter(t => t && String(t.quote || '').trim()),
    settings: { owner_name: by.owner_name || '', owner_email: by.owner_email || '', about: by.bid_about || '' },
  }
}

/**
 * A brand-new proposal's document, started from a preset.
 *
 * The opening sections, the preset applied, and the quote worked out and
 * frozen exactly as the studio does on every change, so the row is inserted
 * with the total the proposal list and pipeline read. Nobody has named the
 * proposal yet, so it always takes the package's name.
 */
export function proposalFromPreset(preset, catalog = {}, { recipient = '' } = {}) {
  const fresh = { kind: 'proposal', title: '', recipient, sections: startingSections('proposal') }
  const { doc, added, missing } = applyPreset(fresh, preset, catalog)
  const priced = withTotals(doc, { policies: catalog.policies || [] })
  const investment = priced.sections.find(s => s.type === 'investment')?.data || {}
  return { title: priced.title, sections: priced.sections, total: investmentTotal(investment), added, missing }
}

// What a lead asked for, as a starting package: the program their request
// points to (leadPipeline.impliedProgram) mapped to the package that opens it.
// A suggestion only — the dialog shows it chosen, and anything can be picked.
const PRESET_FOR_PROGRAM = {
  'brand-strategy': 'positioning-sprint',
  'brand-identity': 'brand-foundation',
  'website-growth': 'website-growth',
  'marketing-leadership': 'fractional-cmo',
}

export function presetForLead(lead) {
  const program = impliedProgram(lead)
  return (program && PRESET_FOR_PROGRAM[program.key]) || null
}
